// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component, Prop, Vue } from 'vue-property-decorator';
import { BootstrapBadge, BootstrapButton, BootstrapImage, BootstrapLink } from '../../../dsl/front';
import { ImageRatio } from '../../_contracts';
import { prepareImage } from '../../_filters';
import { ArticleAuthor } from '../ArticleAuthor';
import { PlayIcon } from '../PlayIcon';
import { TileSize } from './Tile.contracts';
import { tileSizeClassRegistry } from './Tile.config';
/**
 * @author Sebastian Dziechciarz <sebastian.dziechciarz@movecloser.pl>
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
let Tile = class Tile extends Vue {
    constructor() {
        super(...arguments);
        /**
         * Component's UUID.
         */
        this.id = null;
        /**
         * Determines whether the YouTube player has been initialised.
         */
        this.isPlayerInitialised = false;
    }
    /**
     * Article's author data.
     */
    get author() {
        if (typeof this.article.author === 'undefined') {
            return;
        }
        return this.article.author;
    }
    /**
     * Article author's avatar.
     */
    get authorAvatar() {
        if (typeof this.author === 'undefined' || typeof this.author.avatar === 'undefined') {
            return;
        }
        return prepareImage(this.author.avatar, ImageRatio.Square, 28, this.isLazy);
    }
    /**
     * Determines whether the article's category should be rendered.
     */
    get canShowCategory() {
        if (this.size === TileSize.S) {
            return false;
        }
        return !!(this.showCategory && this.hasParent);
    }
    /**
     * Determines whether the article has its corresponding video.
     */
    get hasVideo() {
        return typeof this.article.properties.cover === 'object' && this.article.properties.cover !== null &&
            typeof this.article.properties.cover.video !== 'undefined' && this.article.properties.cover.video !== null &&
            ((typeof this.article.properties.cover.video.url === 'string' && this.article.properties.cover.video.url.length > 0) ||
                (typeof this.article.properties.cover.video.onNetworkUrl === 'string' && this.article.properties.cover.video.onNetworkUrl.length > 0) ||
                (!!this.article.properties.cover.video.file && typeof this.article.properties.cover.video.file === 'object' &&
                    this.article.properties.cover.video.file.value > 0 &&
                    (!!this.article.properties.cover.video.file.url || !!this.article.properties.cover.video.file.m3u8)));
    }
    /**
     * Determines whether the title is longer than shorter 🙈
     */
    get isTitleLong() {
        switch (this.size) {
            case TileSize.XL: {
                return this.title.length > 60;
            }
            case TileSize.L: {
                return this.title.length > 50;
            }
            case TileSize.S:
            default: {
                return this.title.length > 70;
            }
        }
    }
    /**
     * TODO: Description.
     */
    get labels() {
        if (!this.hasLabels) {
            return [];
        }
        return this.article.properties.labels;
    }
    /**
     * TODO: Description.
     */
    get title() {
        if (!this.titleVersion) {
            return this.article.title;
        }
        if (typeof this.article.properties.additionalTitles !== 'object' || this.article.properties.additionalTitles === null ||
            !this.article.properties.additionalTitles[this.titleVersion]) {
            return this.article.title;
        }
        return this.article.properties.additionalTitles[this.titleVersion];
    }
    /**
     * Maximum number of lines for the tile's title.
     */
    get titleMaxLines() {
        switch (this.size) {
            case TileSize.XL: {
                return this.isTitleLong ? 3 : 2;
            }
            case TileSize.L: {
                return this.isTitleLong ? 4 : 3;
            }
            case TileSize.S:
            default: {
                return this.isTitleLong ? 4 : 3;
            }
        }
    }
    get shouldShowPublicationDate() {
        return this.showPublicationDate && this.article.publicationDate !== null;
    }
    /**
     * Determines whether the `<ArticleAuthor>` component should be rendered.
     */
    get showArticleAuthor() {
        return this.showAuthorAvatar || this.showAuthorName || this.showPublicationDate;
    }
    /**
     * The CSS class defining the tile's sizing variant.
     */
    get sizeClass() {
        if (typeof tileSizeClassRegistry[this.size] !== 'string') {
            console.warn(`TileModuleUi.sizeClass(): Provided registry does NOT contain the given key.\nFalling back to the first key in the set, which is [${Object.keys(tileSizeClassRegistry)[0]}].`);
            return Object.values(tileSizeClassRegistry)[0];
        }
        return tileSizeClassRegistry[this.size];
    }
    /**
     * Applicable `Image` for the current screen resolution.
     */
    get thumbnail() {
        if (typeof this.article.properties.cover !== 'object' || typeof this.article.properties.cover === null ||
            typeof this.article.properties.cover.image !== 'object' || this.article.properties.cover.image === null) {
            return {
                src: '',
                width: null,
                height: null
            };
        }
        let ratio = ImageRatio.Tile;
        if (this.size === TileSize.XL) {
            ratio = this.isMobile ? ImageRatio.Square : ImageRatio.Wide;
        }
        else if (this.size === TileSize.L && this.isMobile) {
            ratio = ImageRatio.Square;
        }
        const maxWidth = this.isMobile
            ? 768
            : this.size === TileSize.XL
                ? 1220
                : this.size === TileSize.L ? 587 : 390;
        return prepareImage(this.article.properties.cover.image, ratio, maxWidth, this.isLazy);
    }
    /**
     * Determines whether there are any labels to be rendered.
     */
    get hasLabels() {
        return (this.article.properties.labels !== null &&
            Array.isArray(this.article.properties.labels) &&
            this.article.properties.labels.length > 0);
    }
    /**
     * Determines whether there's a parent category to be rendered.
     */
    get hasParent() {
        return (typeof this.article.parent !== 'undefined' &&
            this.article.parent !== null &&
            typeof this.article.parent.name === 'string' &&
            this.article.parent.name.length > 0);
    }
};
__decorate([
    Prop({
        type: Object,
        required: true
    })
], Tile.prototype, "article", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: ''
    })
], Tile.prototype, "fallbackImage", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], Tile.prototype, "isLazy", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], Tile.prototype, "isMobile", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "reverseContent", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "showAuthorAvatar", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "showAuthorName", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "showArticleTags", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "showCategory", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "showPublicationDate", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "showTitle", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: TileSize.S
    })
], Tile.prototype, "size", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], Tile.prototype, "titleOnImage", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: 'h3'
    })
], Tile.prototype, "titleTag", void 0);
__decorate([
    Prop({
        type: String,
        required: false
    })
], Tile.prototype, "titleVersion", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "isPremium", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], Tile.prototype, "windowService", void 0);
Tile = __decorate([
    Component({
        name: 'Tile',
        components: {
            ArticleAuthor,
            BootstrapBadge,
            BootstrapButton,
            BootstrapImage,
            BootstrapLink,
            PlayIcon
        },
        template: `
    <div class="tile position-relative"
         :class="[sizeClass, {
           '--title-below': !titleOnImage,
           '--outlined': typeof article.properties.cover === 'object' && article.properties.cover.extraDistinction,
           '--small-title': isTitleLong, '--reverse-content': reverseContent
         }]">

    <!-- Thumbnail -->
    <figure class="tile__thumbnail">
      <!-- Actual image -->
      <BootstrapImage v-bind="{ ...thumbnail, fallback: fallbackImage }" class="tile__thumbnail__image"/>

      <span v-if="article.paywall" class="premium__badge">premium</span>

      <span v-if="article.sponsored" class="sponsored__badge"
            :class="{ '--bottom': article.paywall }">sponsorowane</span>

      <figcaption class="tile__thumbnail__content-wrapper" :class="{ '--no-gradient': !titleOnImage }">
        <!-- "Play" button -->
        <span v-if="!reverseContent && hasVideo" class="tile__play-btn p-0 shadow-none --disabled"
              :style="{ zIndex: isPlayerInitialised ? 'auto' : 4 }">
          <PlayIcon/>
        </span>

        <!-- Labels -->
        <ul v-if="showArticleTags" class="tile__labels list-inline">
          <li v-for="(label, index) in labels" :key="label.name + index"
              class="tile__label list-inline-item">
            <BootstrapBadge :theme="label.color">
              {{ label.name }}
            </BootstrapBadge>
          </li>
        </ul>
        <!-- END | Labels -->

        <!-- Category -->
        <span v-if="canShowCategory" class="tile__category">
            {{ article.parent.name }}
          </span>
        <!-- END | Category -->

        <!-- Title (on top of the image) -->
        <BootstrapLink v-if="showTitle && titleOnImage" :link="{ target: article.urlPath }"
                       class="tile__title"
                       :class="{'stretched-link' : !isPlayerInitialised }"
                       v-clamp="titleMaxLines">
          {{ title }}
        </BootstrapLink>

        <div class="d-flex">
          <!-- "Play" button -->
          <span v-if="reverseContent && hasVideo" class="tile__play-btn p-0 shadow-none me-2 --disabled"
                :style="{ zIndex: isPlayerInitialised ? 'auto' : 4 }"
          >
            <PlayIcon/>
          </span>
          <!-- Author -->
          <ArticleAuthor v-if="author && showArticleAuthor"
                         v-bind="{ showAuthorName, showPublicationDate: shouldShowPublicationDate }"
                         :avatar="authorAvatar"
                         :name="author.firstName + ' ' + author.lastName"
                         :publicationDate="article.publicationDate"
                         :showAuthorAvatar="authorAvatar && showAuthorAvatar"
                         class="tile__author me-5" shortenDate/>

        </div>
        <!-- END | Labels -->
      </figcaption>
    </figure>
    <!-- END | Thumbnail -->

    <!-- Title (below the image) -->
    <BootstrapLink v-if="showTitle && !titleOnImage" :link="{ target: article.urlPath }"
                   class="tile__title" :class="{'stretched-link' : !isPlayerInitialised }"
                   v-clamp="titleMaxLines">
      {{ title }}
    </BootstrapLink>

    <BootstrapLink v-if="!showTitle" :link="{ target: article.urlPath }"
                   class="tile__title"
                   :class="{'stretched-link' : !isPlayerInitialised }"
                   v-clamp="titleMaxLines"/>

    </div>
  `
    })
], Tile);
export { Tile };
